import { supabase } from '@/utils/supabase';
import { getSecaoAtivaManual } from '@/utils/beaaContextoSecao';
import { buscarManualSemantico, registrarPerguntaBeaa } from '@/utils/beaaRag';
import {
  beaaConsoleGeralProfile,
  normalizeBeaaText,
  pontuarEntrada,
  type BeaaAssistantAction,
  type BeaaAssistantEntry,
} from '@/utils/beaaAssistant';

/** Um artigo de manual vindo do banco (tabela petbea_manual_artigos). */
export interface ArtigoManual {
  id: string;
  titulo: string;
  categoria: string;
  conteudo: string;
  tags: string[] | null;
  ordem: number | null;
  rota: string | null;
  secao: string | null;
  /** Ambiente do artigo: manuais internos do Console ou guia do portal. */
  ambiente?: string | null;
  /** Ações (botões) prontas do artigo, quando definidas no banco. */
  acoes?: BeaaAssistantAction[] | null;
}

const COLUNAS = 'id, titulo, categoria, conteudo, tags, ordem, rota, secao, ambiente, acoes';

/**
 * Bônus de pontuação para o artigo da seção visível no momento.
 * Pequeno de propósito: desempata a favor do contexto, mas não atropela
 * um assunto claramente diferente que o usuário perguntou.
 */
const BONUS_SECAO_ATIVA = 4;

/**
 * Similaridade mínima (cosseno) para a Beaa confiar na busca por significado.
 * Abaixo disso, ela cai no retrocesso por palavra-chave — evitando responder
 * algo errado quando o assunto é claramente fora do manual.
 */
const LIMIAR_SEMANTICO = 0.5;

/** Folga do limiar quando o artigo é justamente o da seção visível na tela. */
const FOLGA_SECAO_SEMANTICA = 0.08;

let cacheArtigos: ArtigoManual[] | null = null;
let promessaCarregamento: Promise<ArtigoManual[]> | null = null;

/**
 * Carrega os artigos de manual publicados (com cache em memória).
 * Nunca lança erro: em caso de falha devolve lista vazia e a Beaa segue
 * respondendo com o conhecimento fixo do Console.
 */
export async function carregarArtigosManual(): Promise<ArtigoManual[]> {
  if (cacheArtigos) return cacheArtigos;
  if (promessaCarregamento) return promessaCarregamento;

  promessaCarregamento = (async () => {
    try {
      const { data, error } = await supabase
        .from('petbea_manual_artigos')
        .select(COLUNAS)
        .eq('publicado', true)
        .order('ordem', { ascending: true });
      if (error) throw error;
      cacheArtigos = (data ?? []) as ArtigoManual[];
    } catch {
      cacheArtigos = [];
    } finally {
      promessaCarregamento = null;
    }
    return cacheArtigos;
  })();

  return promessaCarregamento;
}

/** Limpa o cache em memória (útil quando novos artigos são gravados). */
export function limparCacheArtigosManual(): void {
  cacheArtigos = null;
}

/** Monta as ações de navegação de um artigo: ir ao trecho do manual e abrir a tela. */
function acoesDoArtigo(artigo: ArtigoManual): BeaaAssistantAction[] {
  // Se o artigo já traz botões prontos (ex.: guia do portal), usa-os.
  const prontas = (artigo.acoes ?? []).filter(
    (acao) => acao && (acao.to || acao.href),
  ) as BeaaAssistantAction[];
  if (prontas.length > 0) return prontas;

  const acoes: BeaaAssistantAction[] = [];
  if (artigo.rota && artigo.secao) {
    acoes.push({
      label: 'Ver no manual',
      icon: 'ri-book-open-line',
      to: `${artigo.rota}/manual#${artigo.secao}`,
    });
  }
  if (artigo.rota) {
    acoes.push({
      label: 'Abrir a tela',
      icon: 'ri-arrow-right-line',
      to: artigo.rota,
    });
  }
  return acoes;
}

/** Converte um artigo do banco na entrada de conhecimento da Beaa. */
export function artigoParaEntrada(artigo: ArtigoManual): BeaaAssistantEntry {
  const palavrasTitulo = artigo.titulo.split(/\s+/).filter((p) => p.length >= 3);
  const keywords = [...(artigo.tags ?? []), ...palavrasTitulo];
  return {
    id: artigo.id,
    keywords,
    answer: artigo.conteudo,
    actions: acoesDoArtigo(artigo),
  };
}

/**
 * Resolve a resposta da Beaa no Console: concorre o conhecimento fixo do Console
 * com os artigos de manual do banco e devolve o que casar melhor.
 *
 * Além da pergunta, considera a seção do manual visível na tela: o artigo
 * daquela seção ganha um bônus de pontuação — e, se a pergunta não casar com
 * nada, é ele quem responde. Assim a Beaa fica "variável por seção", sem clique.
 */
export async function resolverRespostaConsole(pergunta: string): Promise<BeaaAssistantEntry> {
  const normalizada = normalizeBeaaText(pergunta);
  const secaoAtiva = getSecaoAtivaManual();

  if (!normalizada) {
    const artigoSecao = await artigoDaSecao(secaoAtiva?.id);
    return artigoSecao ? artigoParaEntrada(artigoSecao) : beaaConsoleGeralProfile.fallback;
  }

  // 1) RAG por significado: compara o sentido da pergunta com o conteúdo
  //    dos artigos do banco (embeddings + pgvector). É o coração do "por
  //    significado" — funciona mesmo com palavras diferentes do manual.
  const semanticos = await buscarManualSemantico(pergunta);
  if (semanticos && semanticos.length > 0) {
    const melhorSemantico = semanticos[0];
    const daSecao = secaoAtiva
      ? semanticos.find((artigo) => artigo.secao === secaoAtiva.id)
      : undefined;

    const escolhido = daSecao ?? melhorSemantico;
    const similaridade = escolhido.similarity ?? 0;
    const limiar = daSecao ? LIMIAR_SEMANTICO - FOLGA_SECAO_SEMANTICA : LIMIAR_SEMANTICO;

    if (similaridade >= limiar) {
      return artigoParaEntrada(escolhido);
    }
  }

  // 2) Retrocesso: busca por palavra-chave + contexto de seção (comportamento
  //    anterior). Garante resposta mesmo sem embeddings indexados ou offline.
  let melhor: BeaaAssistantEntry = beaaConsoleGeralProfile.fallback;
  let melhorPontuacao = 0;

  for (const entrada of beaaConsoleGeralProfile.knowledge) {
    const pontos = pontuarEntrada(entrada, normalizada);
    if (pontos > melhorPontuacao) {
      melhorPontuacao = pontos;
      melhor = entrada;
    }
  }

  const artigos = await carregarArtigosManual();
  let artigoSecaoAtiva: ArtigoManual | null = null;

  for (const artigo of artigos) {
    const daSecaoAtiva = Boolean(secaoAtiva && artigo.secao === secaoAtiva.id);
    if (daSecaoAtiva && !artigoSecaoAtiva) artigoSecaoAtiva = artigo;

    const entrada = artigoParaEntrada(artigo);
    let pontos = pontuarEntrada(entrada, normalizada);
    if (daSecaoAtiva && pontos > 0) pontos += BONUS_SECAO_ATIVA;

    if (pontos > melhorPontuacao) {
      melhorPontuacao = pontos;
      melhor = entrada;
    }
  }

  // A pergunta não casou com nada, mas há uma seção visível:
  // responde com o manual daquela seção (o "já direciona").
  if (melhorPontuacao === 0 && artigoSecaoAtiva) {
    return artigoParaEntrada(artigoSecaoAtiva);
  }

  // Caiu no "não sei": registra a pergunta para priorizar novos artigos.
  if (melhor.id === beaaConsoleGeralProfile.fallback.id) {
    void registrarPerguntaBeaa('console', normalizada, secaoAtiva?.id ?? 'geral');
  }

  return melhor;
}

/** Busca, entre os artigos, o que pertence a uma seção (id da âncora). */
async function artigoDaSecao(secao: string | null | undefined): Promise<ArtigoManual | null> {
  if (!secao) return null;
  const artigos = await carregarArtigosManual();
  return artigos.find((artigo) => artigo.secao === secao) ?? null;
}